/**
 * Locked defaults and merge helper for the cost-model configuration tabs.
 *
 * The values mirror the backend module_configs *_CONFIG_DEFAULT dicts so a fresh
 * screen, or a record predating a block, loads the same numbers the backend
 * back-fills. The difficulty-multiplier keys match the backend NRE default
 * (api, spreadsheet, scannedPdf, handwritten); a source whose arrival type is
 * not in this dict is rejected by the calculator, so only these rows are shown.
 */

import { ARRIVAL_TYPES } from '../../proposals/components/modules/moduleDefs';
import { ModuleConfigs } from './moduleConfigTypes';

export const DEFAULT_DIFFICULTY_MULTIPLIERS: Record<string, number> = {
  api: 0.5,
  spreadsheet: 1.0,
  scannedPdf: 2.0,
  handwritten: 3.0,
};

/**
 * Per-format extraction-compute multiplier, applied to the compute line only.
 * Kept separate from the labour difficulty map: api stays 1.0 for compute even
 * though it is 0.5 for labour. Harder formats cost more to process per document.
 */
export const DEFAULT_COMPUTE_MULTIPLIERS: Record<string, number> = {
  api: 1.0,
  nativeDigital: 1.0,
  spreadsheet: 1.0,
  scannedPdf: 2.0,
  handwritten: 3.0,
};

/** Arrival types whose difficulty multiplier the NRE tab edits. */
export const DIFFICULTY_MULTIPLIER_ROWS = ARRIVAL_TYPES.filter(
  (arrival) => arrival.value in DEFAULT_DIFFICULTY_MULTIPLIERS
);

/** Arrival types whose compute multiplier the module tabs edit. */
export const COMPUTE_MULTIPLIER_ROWS = ARRIVAL_TYPES.filter(
  (arrival) => arrival.value in DEFAULT_COMPUTE_MULTIPLIERS
);

export const MODULE_CONFIG_DEFAULTS: ModuleConfigs = {
  material_config: {
    reportsPerPipe: 19.0,
    autoExtractRate: 0.98,
    minutesPerException: 4.0,
    hourlyRate: 100.0,
    computeCostPerDoc: 0.04,
    computeMultiplierByFormat: { ...DEFAULT_COMPUTE_MULTIPLIERS },
    storagePerPipe: 0.04,
    floorPerPipe: 0.5,
    ceilingPerPipe: 0.0,
    qaOverheadPct: 0.1,
    marginTarget: 0.75,
    gasAddOnFee: 2000.0,
    wireAddOnFee: 2000.0,
    coatingMaterialsAddOnFee: 3000.0,
    defaultServiceTier: 'extended',
    moduleFloor: 20000.0,
  },
  pipefitter_config: {
    technicianDayRate: 800.0,
    scannerDayCost: 150.0,
    pipesPerDay: 300.0,
    scanMargin: 0.5,
    mobilization: 10000.0,
    pipeDataPerJoint: 4.0,
    pipeCompatibilityPerJoint: 2.0,
    optiFitPerJoint: 10.0,
    omniFitPerJoint: 15.0,
    captureShare: 0.1,
    gainShareFloor: 5.0,
    optiFitCap: 30.0,
    omniFitCap: 50.0,
    moduleFloor: 20000.0,
  },
  ndt_config: {
    reportsPerInspection: 1.0,
    autoExtractRate: 0.98,
    minutesPerException: 4.0,
    hourlyRate: 100.0,
    computeCostPerDoc: 0.04,
    computeMultiplierByFormat: { ...DEFAULT_COMPUTE_MULTIPLIERS },
    aiComputeCostPerDoc: 0.2,
    storageCostPerInspection: 0.05,
    qaOverheadPct: 0.1,
    marginTarget: 0.75,
    floorPerInspection: 0.5,
    ceilingPerInspection: 8.0,
    moduleFloor: 20000.0,
  },
  coating_config: {
    reportsPerJoint: 1.0,
    autoExtractRate: 0.98,
    minutesPerException: 4.0,
    hourlyRate: 100.0,
    computeCostPerDoc: 0.04,
    computeMultiplierByFormat: { ...DEFAULT_COMPUTE_MULTIPLIERS },
    storageCostPerJoint: 0.04,
    qaOverheadPct: 0.1,
    marginTarget: 0.75,
    floorPerJoint: 0.5,
    ceilingPerJoint: 8.0,
    moduleFloor: 20000.0,
  },
  nre_config: {
    setupHours: 8.0,
    perUnitHours: 2.0,
    difficultyMultipliers: { ...DEFAULT_DIFFICULTY_MULTIPLIERS },
    validationOverhead: 0.25,
    engineerRate: 100.0,
    marginTarget: 0.75,
  },
  platform_config: {
    baseInfraMonthly: 500.0,
    perModuleInfraMonthly: 60.0,
    baseSupportHours: 40.0,
    supportHourlyRate: 40.0,
    perUserMonthly: 40.0,
    marginTarget: 0.75,
  },
  retention_config: {
    coldArchiveAnnualFee: 3000.0,
    warmAccessAnnualFee: 15000.0,
  },
  bolton_hardware_config: {
    unitCost: 0.0,
    utilization: 100.0,
    profitMargin: 0.0,
    rentalRate: 0.0,
  },
};

/**
 * Merge saved blocks from the active configuration onto the defaults, so a
 * record predating a block (or missing a key) still loads cleanly.
 */
export const mergeModuleConfigs = (
  apiConfig: Partial<ModuleConfigs> | null | undefined
): ModuleConfigs => ({
  material_config: {
    ...MODULE_CONFIG_DEFAULTS.material_config,
    ...(apiConfig?.material_config ?? {}),
    computeMultiplierByFormat: {
      ...MODULE_CONFIG_DEFAULTS.material_config.computeMultiplierByFormat,
      ...(apiConfig?.material_config?.computeMultiplierByFormat ?? {}),
    },
  },
  pipefitter_config: {
    ...MODULE_CONFIG_DEFAULTS.pipefitter_config,
    ...(apiConfig?.pipefitter_config ?? {}),
  },
  ndt_config: {
    ...MODULE_CONFIG_DEFAULTS.ndt_config,
    ...(apiConfig?.ndt_config ?? {}),
    computeMultiplierByFormat: {
      ...MODULE_CONFIG_DEFAULTS.ndt_config.computeMultiplierByFormat,
      ...(apiConfig?.ndt_config?.computeMultiplierByFormat ?? {}),
    },
  },
  coating_config: {
    ...MODULE_CONFIG_DEFAULTS.coating_config,
    ...(apiConfig?.coating_config ?? {}),
    computeMultiplierByFormat: {
      ...MODULE_CONFIG_DEFAULTS.coating_config.computeMultiplierByFormat,
      ...(apiConfig?.coating_config?.computeMultiplierByFormat ?? {}),
    },
  },
  nre_config: {
    ...MODULE_CONFIG_DEFAULTS.nre_config,
    ...(apiConfig?.nre_config ?? {}),
    difficultyMultipliers: {
      ...MODULE_CONFIG_DEFAULTS.nre_config.difficultyMultipliers,
      ...(apiConfig?.nre_config?.difficultyMultipliers ?? {}),
    },
  },
  platform_config: {
    ...MODULE_CONFIG_DEFAULTS.platform_config,
    ...(apiConfig?.platform_config ?? {}),
  },
  retention_config: {
    ...MODULE_CONFIG_DEFAULTS.retention_config,
    ...(apiConfig?.retention_config ?? {}),
  },
  bolton_hardware_config: {
    ...MODULE_CONFIG_DEFAULTS.bolton_hardware_config,
    ...(apiConfig?.bolton_hardware_config ?? {}),
  },
});
